import { HMIField, HMITemplate } from './types';

/**
 * Converts a BMT Test_Info.ini-style recipe export - grouped by UI section
 * label, e.g. `{ "Heating - Infrared": { "Oven power (P)": 70, ... } }`
 * (see the sample tagged `bmt file.json`) - into the flat `fieldId -> value`
 * map the HMI renderer understands, by matching each key to the `process`
 * screen field whose `testInfoKey` equals it (see `defaultFieldsData.json`).
 * Unmatched keys (sections/fields the current template doesn't define) are
 * silently skipped rather than failing the whole import.
 */
export function recipeJsonToFieldValues(
  template: HMITemplate,
  recipe: Record<string, Record<string, unknown>>
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  const processFields = template.fields.filter((f) => f.screen === 'process' && f.testInfoKey);

  Object.values(recipe).forEach((section) => {
    if (!section || typeof section !== 'object') return;
    Object.entries(section).forEach(([key, raw]) => {
      const field = processFields.find((f) => f.testInfoKey === key);
      if (!field) return;
      values[field.id] = coerceRecipeValue(field, raw);
    });
  });

  return values;
}

function coerceRecipeValue(field: HMIField, raw: unknown): unknown {
  if (field.datatype === 'array') {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      // BMT exports array tags as a ';'-separated string, sometimes with a
      // trailing CR/LF escape sequence (e.g. "85;75;0...\0D\0A") - parseInt
      // naturally stops at the first non-digit character per token.
      return raw
        .split(';')
        .map((token) => Number.parseInt(token, 10))
        .filter((n) => !Number.isNaN(n))
        .slice(0, field.arrayLength ?? undefined);
    }
    return [];
  }
  if (field.datatype === 'bool') {
    return raw === true || raw === 1 || raw === '1' || raw === 'true';
  }
  if (field.datatype === 'i32' || field.datatype === 'f32' || field.datatype === 'f64') {
    const n = Number(raw);
    return Number.isNaN(n) ? 0 : n;
  }
  return raw;
}
